import { useMemo } from "react";
import "./MindMappingReportBEighthSection.css";

/* ── Sector-type competitive landscape data ─────────────────── */
const SECTOR_LANDSCAPE = {
  tertiary: {
    density:    "High — well-funded incumbents exist in most service niches",
    maturity:   "Growth to Mature — many verticals are actively being disrupted",
    blueOcean:  "Hyper-niche B2B tools, rural service delivery, government tech modernization",
    entryBarrier:"Low to Medium — primary barriers are customer acquisition and trust",
    strategy:   "Differentiation — identify a specific sub-segment where incumbents underserve",
    moatType:   "Network effects, proprietary data, switching costs (SaaS lock-in)",
    timing:     "Now — window is open; 2-3 years before consolidation in most niches",
  },
  secondary: {
    density:    "Medium — fragmented with regional players; few national scale-ups",
    maturity:   "Early to Growth — digital adoption just beginning in most sub-sectors",
    blueOcean:  "SME digitization tools, supply-chain visibility, quality control automation",
    entryBarrier:"Medium to High — capital requirements, domain expertise, regulatory approvals",
    strategy:   "First-Mover in niche — pick a specific manufacturing sub-vertical and go deep",
    moatType:   "IP, proprietary processes, long-term contracts, specialized talent",
    timing:     "Early — PLI schemes and Make-in-India tailwinds create a 3-5 year window",
  },
  primary: {
    density:    "Low — few organized players; highly fragmented and informal incumbents",
    maturity:   "Nascent — most digitization is at pilot stage; scale has not been proven",
    blueOcean:  "Farmer advisory platforms, supply chain transparency, agri-fintech, cold storage logistics",
    entryBarrier:"Low — but distribution and trust-building are slow; requires on-ground presence",
    strategy:   "First-Mover with hyperlocal model — district-level density before geographic expansion",
    moatType:   "Distribution network, government partnerships, farmer/producer relationships",
    timing:     "Now — RKVY, NABARD, and PM Agri programs are funding early-stage pilots",
  },
};

/* ── Course-based unfair advantage ──────────────────────────── */
function getCourseAdvantage(course, sector) {
  if (!course) return `Domain knowledge from your course provides a credibility edge when approaching ${sector} customers.`;
  const c = course.toLowerCase();
  if (/b\.?tech|b\.?e|engineering/i.test(c))
    return `Engineering background gives a technical moat in ${sector} — prioritize building technically defensible products.`;
  if (/mba|bba/i.test(c))
    return `Business education in ${sector} accelerates go-to-market and investor relations — leverage your market-sizing and business model expertise.`;
  if (/b\.?sc|msc|science/i.test(c))
    return `Science background brings research rigour to ${sector} — ideal for data-driven or IP-backed startup models.`;
  if (/bca|mca|computer/i.test(c))
    return `Tech background enables rapid prototyping in ${sector} — use this to build MVPs fast and iterate on real user feedback.`;
  if (/b\.?com|finance|account/i.test(c))
    return `Financial expertise gives edge in ${sector} fintech or compliance plays — investors value founders who understand unit economics.`;
  if (/design|b\.?des|bfa/i.test(c))
    return `Design-led thinking creates differentiation in ${sector} consumer products — invest in UX research as a competitive weapon.`;
  return `Your educational background creates a unique lens on ${sector} problems — use domain expertise to unlock insights competitors miss.`;
}

/* ── Age-based market entry timing ──────────────────────────── */
function getTimingAdvice(age) {
  const n = parseInt(age, 10);
  if (isNaN(n)) return "Your current stage is ideal for market exploration and early validation.";
  if (n < 26)   return "Early entry advantage — low personal financial pressure enables aggressive experimentation. Build your first venture as a learning vehicle.";
  if (n <= 32)  return "Prime founder window — combine energy with emerging domain expertise. Raise early, hire fast, and capture the market before it matures.";
  if (n <= 42)  return "Experience advantage — deep domain knowledge and a professional network compress the time-to-market. Target B2B or enterprise with credibility-first GTM.";
  return "Seasoned operator edge — credibility, networks, and capital access exceed peers. Focus on adjacent innovation to established expertise, not zero-to-one bets.";
}

const MindMappingReportBEighthSection = ({ res, toggle }) => {
  const sectorType = (res?.sector_type ?? "tertiary").toLowerCase();
  const landscape  = SECTOR_LANDSCAPE[sectorType] ?? SECTOR_LANDSCAPE.tertiary;
  const sector     = res?.sector ?? "your sector";
  const courseAdv  = useMemo(() => getCourseAdvantage(res?.course, sector), [res?.course, sector]);
  const timingAdv  = useMemo(() => getTimingAdvice(res?.age), [res?.age]);

  const rows = [
    {
      sno: "8 A", factor: "Market Competition Density",
      profile: `${res?.sector_type ?? "Tertiary"} Sector`,
      signal: landscape.density,
    },
    {
      sno: "", factor: "Market Maturity Stage",
      profile: sector,
      signal: landscape.maturity,
    },
    {
      sno: "", factor: "Blue Ocean Opportunities",
      profile: `${res?.sector_type ?? "Tertiary"} sector gaps`,
      signal: landscape.blueOcean,
    },
    {
      sno: "", factor: "Market Entry Barrier",
      profile: `${res?.affordability ?? "Standard"} initial fund`,
      signal: landscape.entryBarrier,
    },
    {
      sno: "", factor: "Recommended Entry Strategy",
      profile: `${res?.sector_type ?? "Tertiary"} sector`,
      signal: landscape.strategy,
    },
    {
      sno: "", factor: "Defensible Moat Type",
      profile: `${sector} startup`,
      signal: landscape.moatType,
    },
    {
      sno: "", factor: "Market Entry Timing",
      profile: `${sector} in ${res?.state ?? "India"}`,
      signal: landscape.timing,
    },
    {
      sno: "", factor: "Course-Based Unfair Advantage",
      profile: res?.course ?? "General background",
      signal: courseAdv,
    },
    {
      sno: "", factor: "Founder Age vs Timing Fit",
      profile: res?.age ? `Age ${res.age}` : "Founder profile",
      signal: timingAdv,
    },
  ];

  return (
    <div className="mm-rb-eighth-container">
      <div className={toggle ? "table-adjust" : "mm-rb-eighth-final-table"}>
        <span className="mm-rb-eighth-corner mm-rb-eighth-corner-top-left" />
        <span className="mm-rb-eighth-corner mm-rb-eighth-corner-top-right" />
        <span className="mm-rb-eighth-corner mm-rb-eighth-corner-bottom-left" />
        <span className="mm-rb-eighth-corner mm-rb-eighth-corner-bottom-right" />

        <div className="mm-rb-eighth-module-title">
          <span className="module-prefix">Module 8:</span> Competitive Landscape &amp; Market Entry Strategy
          <span className="mm-rb-premium-tag">★ Report A+ Exclusive</span>
        </div>

        <div className="mm-rb-eighth-table-scroll-wrapper">
          <table className="mm-rb-eighth-inner-table">
            <thead>
              <tr>
                <th className="mm-rb-eighth-col-sno">S.NO</th>
                <th>Competitive Factor</th>
                <th>Your Profile Context</th>
                <th>Market Signal &amp; Recommendation</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  {i === 0 && (
                    <td className="mm-rb-eighth-col-sno" rowSpan={rows.length}>8 A</td>
                  )}
                  <td><strong>{row.factor}</strong></td>
                  <td className="mm-rb-eighth-user-val">{row.profile}</td>
                  <td className="mm-rb-eighth-highlight">{row.signal}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MindMappingReportBEighthSection;
